import { Component } from '@theme/component';

/**
 * Wishlist — no app, no account needed (design: heart on every card and on
 * the product page, "Wishlist" in the header).
 *
 *   WishlistStore     the single source of truth: product handles in
 *                     localStorage, change events, sync across open tabs
 *   <wishlist-button> heart toggle (snippets/custom-wishlist-button.liquid)
 *   <wishlist-count>  header badge with the number of saved products
 *   <wishlist-grid>   the wishlist page (sections/custom-wishlist.liquid):
 *                     fetches each saved product's card from
 *                     /products/<handle>?view=wishlist-card, i.e. the exact
 *                     site-wide card rendered by Shopify — no card markup here
 *
 * Every element only talks to the store, never to each other, so a heart
 * clicked anywhere updates every heart, the header count and the page.
 * Storage failures (private mode, blocked cookies) degrade to an in-memory
 * list for the visit instead of throwing.
 */

const STORAGE_KEY = 'q-souq:wishlist';
const MAX_ITEMS = 100;
const CHANGE = 'wishlist:change';

class Store extends EventTarget {
  /** @type {string[]} */
  #handles = [];

  constructor() {
    super();
    this.#handles = this.#read();

    // Another tab changed the list
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#handles = this.#read();
      this.#emit();
    });
  }

  /** Saved handles, most recent first */
  get handles() {
    return [...this.#handles];
  }

  get count() {
    return this.#handles.length;
  }

  /** @param {string} handle */
  has(handle) {
    return this.#handles.includes(handle);
  }

  /** @param {string} handle @returns {boolean} whether it is saved now */
  toggle(handle) {
    if (this.has(handle)) this.remove(handle);
    else this.add(handle);
    return this.has(handle);
  }

  /** @param {string} handle */
  add(handle) {
    if (!handle || this.has(handle)) return;
    this.#handles = [handle, ...this.#handles].slice(0, MAX_ITEMS);
    this.#commit();
  }

  /** @param {string} handle */
  remove(handle) {
    if (!this.has(handle)) return;
    this.#handles = this.#handles.filter((h) => h !== handle);
    this.#commit();
  }

  #commit() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#handles));
    } catch {
      // Storage unavailable: keep the in-memory list for this visit
    }
    this.#emit();
  }

  #emit() {
    this.dispatchEvent(new CustomEvent(CHANGE, { detail: { handles: this.handles } }));
  }

  /** @returns {string[]} */
  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  }
}

/** One store per page, shared by every element below */
export const WishlistStore = /** @type {Store} */ (window.QSouqWishlist ??= new Store());

/**
 * Base for elements that re-render whenever the wishlist changes.
 * @template {import('@theme/component').Refs} T
 * @extends {Component<T>}
 */
class WishlistElement extends Component {
  connectedCallback() {
    super.connectedCallback();
    WishlistStore.addEventListener(CHANGE, this.#onChange);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    WishlistStore.removeEventListener(CHANGE, this.#onChange);
  }

  #onChange = () => this.render();

  /** Override: sync the element with WishlistStore */
  render() {}
}

/**
 * Heart toggle.
 * data-handle, data-label-add, data-label-remove (labels include the title)
 * @extends {WishlistElement<{ button: HTMLButtonElement, status?: HTMLElement }>}
 */
class WishlistButton extends WishlistElement {
  requiredRefs = ['button'];

  /** @param {Event} event */
  toggle(event) {
    // The heart sits on top of a card link: don't open the product
    event.preventDefault();
    event.stopPropagation();

    const saved = WishlistStore.toggle(this.dataset.handle ?? '');
    if (this.refs.status) {
      this.refs.status.textContent = saved ? this.dataset.announceAdded ?? '' : this.dataset.announceRemoved ?? '';
    }
  }

  render() {
    const saved = WishlistStore.has(this.dataset.handle ?? '');
    const { button } = this.refs;
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', (saved ? this.dataset.labelRemove : this.dataset.labelAdd) ?? '');
  }
}

/** Header count badge: hidden at zero */
class WishlistCount extends WishlistElement {
  render() {
    const count = WishlistStore.count;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  }
}

/**
 * Wishlist page grid.
 * @extends {WishlistElement<{ list: HTMLElement, empty: HTMLElement, loading?: HTMLElement }>}
 */
class WishlistGrid extends WishlistElement {
  requiredRefs = ['list', 'empty'];

  /** @type {Map<string, Element>} handle → rendered card */
  #cards = new Map();

  /** Renders run one after another, so a card is never fetched twice */
  #queue = Promise.resolve();

  render() {
    this.#queue = this.#queue.then(() => this.#sync());
  }

  async #sync() {
    const handles = WishlistStore.handles;
    const { list, empty, loading } = this.refs;

    // Drop cards whose product was removed (e.g. heart clicked on this page)
    for (const [handle, card] of this.#cards) {
      if (!handles.includes(handle)) {
        card.remove();
        this.#cards.delete(handle);
      }
    }

    const missing = handles.filter((handle) => !this.#cards.has(handle));
    if (missing.length && loading) loading.hidden = false;

    const results = await Promise.all(missing.map((handle) => this.#fetchCard(handle)));

    results.forEach((card, i) => {
      const handle = missing[i];
      if (!handle) return;
      if (card) this.#cards.set(handle, card);
      // Product deleted or unpublished: forget it
      else WishlistStore.remove(handle);
    });

    // Keep the saved order (most recent first)
    for (const handle of WishlistStore.handles) {
      const card = this.#cards.get(handle);
      if (card) list.append(card);
    }

    if (loading) loading.hidden = true;
    empty.hidden = this.#cards.size > 0;
    list.hidden = this.#cards.size === 0;
  }

  /** @param {string} handle */
  async #fetchCard(handle) {
    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=wishlist-card`);
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return html.querySelector('.resource-list__item');
    } catch {
      return null;
    }
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
